/**
 * Pure state machine for the shared Table/Pitch watchlist preference order.
 *
 * Optimistic operations are retained by identity so one failed save can be
 * removed without discarding later local intent. Server snapshots always own
 * the revision and manual-mode flags.
 */
import { slotCandidatesFor } from "@/features/scouting/constants/primaryPositionSlotCandidates";

/** Authoritative order returned by the backend. */
export interface WatchlistPreferenceSnapshot {
  readonly orderedPlayerIds: readonly string[];
  readonly pitchRevision: number;
  readonly manualOrder: boolean;
  readonly pitchInitialized?: boolean;
  /** Persisted formation identity; absent until the first Pitch read. */
  readonly formation?: string;
  readonly assignmentsByPlayerId?: Readonly<
    Record<string, WatchlistPitchAssignment>
  >;
}

/** Origin of the effective tactical-slot value exposed by Pitch. */
export type WatchlistPitchAssignmentOrigin =
  | "SAVED"
  | "CALCULATED"
  | "UNASSIGNED";

/** Effective tactical-slot projection for one watchlist member. */
export interface WatchlistPitchAssignment {
  readonly tacticalSlot: string | null;
  readonly origin: WatchlistPitchAssignmentOrigin;
}

/** Relative destination for one player move. */
export type WatchlistPreferenceMoveDestination =
  | "top"
  | "bottom"
  | { readonly beforePlayerId: string };

/** One player move retained independently from the order it was created on. */
export interface WatchlistPreferenceMove {
  readonly playerId: string;
  readonly destination: WatchlistPreferenceMoveDestination;
}

/** One optimistic subset operation expressed as relative player moves. */
export interface WatchlistPreferenceOperation {
  readonly kind?: "reorder";
  readonly id: string;
  readonly playerIds: readonly string[];
  readonly moves: readonly WatchlistPreferenceMove[];
}

/** One optimistic tactical-slot assignment. */
export interface WatchlistPitchAssignmentOperation {
  readonly kind: "assignment";
  readonly id: string;
  readonly playerId: string;
  readonly tacticalSlot: string | null;
}

/**
 * One optimistic formation change. `slots` is the target formation's complete
 * slot list, supplied by the caller so the projection mirrors the backend's
 * semantic-slot migration without this module depending on generated enums.
 */
export interface WatchlistPitchFormationOperation {
  readonly kind: "formation";
  readonly id: string;
  readonly formation: string;
  readonly slots: readonly string[];
  /**
   * The slot each placed member held when the change was requested (SE-8391):
   * the Unassigned panel names it as "was {slot}" for the members this
   * formation orphans. Absent on operations created before the panel existed.
   */
  readonly previousSlotByPlayer?: Readonly<Record<string, string>>;
  /**
   * Each member's primary-position code (SE-8606). Present, the projection
   * re-projects members the migration leaves Unassigned into free candidate
   * slots, as the watchlist backend does; absent (scenario pitch), the change
   * is the bare slot-identity migration.
   */
  readonly primaryPositionByPlayer?: Readonly<Record<string, string | null>>;
}

/** Inputs of the R01-J re-projection that follows a formation migration. */
export interface PitchReprojection {
  /** Members in rank order; the higher-ranked member takes a contested slot. */
  readonly rankedPlayerIds: readonly string[];
  readonly primaryPositionByPlayer: Readonly<Record<string, string | null>>;
}

/** Operation kinds serialized through the shared watchlist CAS queue. */
export type WatchlistPitchOperation =
  | WatchlistPreferenceOperation
  | WatchlistPitchAssignmentOperation
  | WatchlistPitchFormationOperation;

/** Save feedback exposed consistently in Table and Pitch. */
export type WatchlistPreferenceStatus =
  | "idle"
  | "saving"
  | "saved"
  | "error"
  | "conflict";

/** One operation kept on screen after its save failed (SE-8389, AC1–AC3). */
export interface WatchlistPreferenceFailure {
  readonly operationId: string;
  /** Consecutive failures of this same operation; three offer Discard. */
  readonly attempts: number;
}

/** State owned once per selected watchlist. */
export interface WatchlistPreferenceOrderState {
  readonly watchlistId: string;
  readonly confirmed: WatchlistPreferenceSnapshot;
  readonly operations: readonly WatchlistPitchOperation[];
  readonly status: WatchlistPreferenceStatus;
  readonly message?: string;
  /** The failed operation still projected on screen, if any. */
  readonly failure?: WatchlistPreferenceFailure;
  /**
   * The operation a stale-version rejection threw out, kept as a copy the
   * user may re-apply once over the other editor's version (D17).
   */
  readonly heldChange?: WatchlistPitchOperation;
  /** Identity of the held copy while its one re-apply is in flight. */
  readonly reapplyingOperationId?: string;
  /** Backend message of a refused re-apply; the copy is gone by then. */
  readonly reapplyRefusal?: string;
  /** Epoch milliseconds of the last successful save ("Saved 2m ago"). */
  readonly savedAt?: number;
  /**
   * Former slot per member orphaned by a formation change whose operation
   * has already settled (SE-8391). Pending operations replay over it; a
   * placement removes the entry. In-memory only — never restored by the
   * server, which forgets the slot along with the assignment.
   */
  readonly formerSlotByPlayerId?: Readonly<Record<string, string>>;
}

/**
 * Creates the empty state used until the first server read arrives.
 * @param watchlistId - Selected watchlist identity
 * @returns Empty preference-order state for that watchlist
 */
export const createPreferenceOrderState = (
  watchlistId: string
): WatchlistPreferenceOrderState => ({
  watchlistId,
  confirmed: {
    orderedPlayerIds: [],
    pitchRevision: 0,
    manualOrder: false,
    pitchInitialized: false,
    assignmentsByPlayerId: {},
  },
  operations: [],
  status: "idle",
});

/**
 * Replaces only the slots occupied by the supplied subset.
 * @param currentOrder - Complete order currently visible to the controller
 * @param requestedSubset - Unique selected members in their new relative order
 * @returns Complete order with every omitted member left in its existing slot
 */
export const applyPreferenceOperation = (
  currentOrder: readonly string[],
  requestedSubset: readonly string[]
): string[] => {
  const requested = new Set(requestedSubset);
  if (
    requestedSubset.length === 0 ||
    requested.size !== requestedSubset.length ||
    requestedSubset.some(playerId => !currentOrder.includes(playerId))
  ) {
    return [...currentOrder];
  }
  const iterator = requestedSubset[Symbol.iterator]();
  return currentOrder.map(playerId =>
    requested.has(playerId) ? (iterator.next().value as string) : playerId
  );
};

/**
 * Finds candidates whose removal explains a single-item permutation.
 * @param currentSubset - Visible players before the interaction
 * @param requestedSubset - Visible players after the interaction
 * @returns Player identities that could have produced the permutation
 */
const findMovedPlayerCandidates = (
  currentSubset: readonly string[],
  requestedSubset: readonly string[]
): readonly string[] =>
  currentSubset.filter(playerId => {
    const withoutCurrent = currentSubset.filter(item => item !== playerId);
    const withoutRequested = requestedSubset.filter(item => item !== playerId);
    return withoutCurrent.every(
      (item, index) => item === withoutRequested[index]
    );
  });

/**
 * Describes a target index independently from the current base order.
 * @param requestedSubset - Visible players in their requested order
 * @param targetIndex - Requested zero-based index
 * @returns Relative destination suitable for later rebasing
 */
const toRelativeDestination = (
  requestedSubset: readonly string[],
  targetIndex: number
): WatchlistPreferenceMoveDestination => {
  if (targetIndex === 0) return "top";
  if (targetIndex === requestedSubset.length - 1) return "bottom";
  return { beforePlayerId: requestedSubset[targetIndex + 1] };
};

/**
 * Creates relative move intent from one rendered subset permutation.
 * @param id - Stable operation identity
 * @param currentOrder - Effective complete order when the interaction starts
 * @param requestedSubset - Visible subset in its requested order
 * @returns Rebasable optimistic operation
 */
export const createPreferenceOperation = (
  id: string,
  currentOrder: readonly string[],
  requestedSubset: readonly string[]
): WatchlistPreferenceOperation => {
  const requestedIds = new Set(requestedSubset);
  const currentSubset = currentOrder.filter(playerId =>
    requestedIds.has(playerId)
  );
  const candidates = findMovedPlayerCandidates(currentSubset, requestedSubset);
  const movedForward = candidates.find(
    playerId =>
      requestedSubset.indexOf(playerId) < currentSubset.indexOf(playerId)
  );
  const movedPlayerId = movedForward ?? candidates[0];
  if (movedPlayerId) {
    return {
      id,
      playerIds: requestedSubset,
      moves: [
        {
          playerId: movedPlayerId,
          destination: toRelativeDestination(
            requestedSubset,
            requestedSubset.indexOf(movedPlayerId)
          ),
        },
      ],
    };
  }

  const transformed = requestedSubset.reduce<{
    readonly working: readonly string[];
    readonly moves: readonly WatchlistPreferenceMove[];
  }>(
    (result, playerId, targetIndex) => {
      const currentIndex = result.working.indexOf(playerId);
      if (currentIndex < 0 || currentIndex === targetIndex) return result;
      const withoutPlayer = [
        ...result.working.slice(0, currentIndex),
        ...result.working.slice(currentIndex + 1),
      ];
      return {
        working: [
          ...withoutPlayer.slice(0, targetIndex),
          playerId,
          ...withoutPlayer.slice(targetIndex),
        ],
        moves: [
          ...result.moves,
          {
            playerId,
            destination: toRelativeDestination(requestedSubset, targetIndex),
          },
        ],
      };
    },
    { working: currentSubset, moves: [] }
  );
  return { id, playerIds: requestedSubset, moves: transformed.moves };
};

export const createPitchAssignmentOperation = (
  id: string,
  playerId: string,
  tacticalSlot: string | null
): WatchlistPitchAssignmentOperation => ({
  kind: "assignment",
  id,
  playerId,
  tacticalSlot,
});

export const isPitchAssignmentOperation = (
  operation: WatchlistPitchOperation
): operation is WatchlistPitchAssignmentOperation =>
  operation.kind === "assignment";

export const createPitchFormationOperation = (
  id: string,
  formation: string,
  slots: readonly string[],
  previousSlotByPlayer?: Readonly<Record<string, string>>,
  primaryPositionByPlayer?: Readonly<Record<string, string | null>>
): WatchlistPitchFormationOperation => ({
  kind: "formation",
  id,
  formation,
  slots,
  ...(previousSlotByPlayer ? { previousSlotByPlayer } : {}),
  ...(primaryPositionByPlayer ? { primaryPositionByPlayer } : {}),
});

export const isPitchFormationOperation = (
  operation: WatchlistPitchOperation
): operation is WatchlistPitchFormationOperation =>
  operation.kind === "formation";

/**
 * Applies the R01 formation rule to saved assignments: a slot survives when
 * the target formation still contains it; otherwise the member is Unassigned
 * with its rank untouched. With a re-projection (the watchlist Pitch since
 * SE-8606), every member Unassigned after that migration then takes the
 * first FREE candidate slot of its primary position, in rank order, one
 * player per slot — so reverting a formation re-places whoever still fits.
 * Placed members never move.
 * @param assignments - Effective assignments before the change
 * @param slots - Complete slot list of the target formation
 * @param reprojection - Rank order and primary positions; absent, no re-projection
 * @returns Assignments after the migration
 */
export const migratePitchAssignments = (
  assignments: Readonly<Record<string, WatchlistPitchAssignment>>,
  slots: readonly string[],
  reprojection?: PitchReprojection
): Readonly<Record<string, WatchlistPitchAssignment>> => {
  const retained = new Set(slots);
  const migrated = Object.fromEntries(
    Object.entries(assignments).map(([playerId, assignment]) => [
      playerId,
      assignment.tacticalSlot && !retained.has(assignment.tacticalSlot)
        ? { tacticalSlot: null, origin: "UNASSIGNED" as const }
        : assignment,
    ])
  );
  return reprojection
    ? reprojectUnassigned(migrated, retained, reprojection)
    : migrated;
};

/**
 * Picks the candidate holding the fewest members; ties go to candidate order.
 * @param candidates - Candidate slots the formation renders, in R01-J order
 * @param counts - Members per slot so far
 * @returns The chosen slot, or undefined when there is no candidate
 */
const leastHeldSlot = (
  candidates: readonly string[],
  counts: Readonly<Record<string, number>>
): string | undefined =>
  candidates.reduce<string | undefined>(
    (best, candidate) =>
      best === undefined || (counts[candidate] ?? 0) < (counts[best] ?? 0)
        ? candidate
        : best,
    undefined
  );

/**
 * Members per slot, over every held assignment.
 * @param assignments - Current assignments
 * @returns Count per slot
 */
const countHeldSlots = (
  assignments: Readonly<Record<string, WatchlistPitchAssignment>>
): Readonly<Record<string, number>> =>
  Object.values(heldSlotsByPlayer(assignments)).reduce<Record<string, number>>(
    (counts, slot) => ({ ...counts, [slot]: (counts[slot] ?? 0) + 1 }),
    {}
  );

/**
 * R01-J over the members a migration left Unassigned (SE-8606): each, in
 * rank order, takes a candidate slot the formation renders. SE-8626: a slot
 * holds any number of targets, so nobody stays Unassigned because a slot is
 * held; with several candidates the member takes the one holding the fewest,
 * ties going to candidate order. A placement is SAVED, as the backend
 * persists it.
 * @param migrated - Assignments after the slot-identity migration
 * @param retained - Slots of the target formation
 * @param reprojection - Rank order and primary positions
 * @param reprojection.rankedPlayerIds - Members in rank order
 * @param reprojection.primaryPositionByPlayer - Primary-position code per member
 * @returns Assignments with the free slots filled
 */
const reprojectUnassigned = (
  migrated: Readonly<Record<string, WatchlistPitchAssignment>>,
  retained: ReadonlySet<string>,
  { rankedPlayerIds, primaryPositionByPlayer }: PitchReprojection
): Readonly<Record<string, WatchlistPitchAssignment>> => {
  const known = new Set(rankedPlayerIds);
  const ranked = [
    ...rankedPlayerIds.filter(playerId => playerId in migrated),
    ...Object.keys(migrated).filter(playerId => !known.has(playerId)),
  ];
  return ranked.reduce<{
    readonly assignments: Readonly<Record<string, WatchlistPitchAssignment>>;
    readonly counts: Readonly<Record<string, number>>;
  }>(
    ({ assignments, counts }, playerId) => {
      if (assignments[playerId]?.tacticalSlot) return { assignments, counts };
      const slot = leastHeldSlot(
        slotCandidatesFor(primaryPositionByPlayer[playerId]).filter(candidate =>
          retained.has(candidate)
        ),
        counts
      );
      return slot
        ? {
            assignments: {
              ...assignments,
              [playerId]: { tacticalSlot: slot, origin: "SAVED" },
            },
            counts: { ...counts, [slot]: (counts[slot] ?? 0) + 1 },
          }
        : { assignments, counts };
    },
    { assignments: migrated, counts: countHeldSlots(migrated) }
  ).assignments;
};

/**
 * Reapplies relative intent over an authoritative or optimistic base order.
 * @param currentOrder - Complete order that receives the operation
 * @param operation - Relative player moves and their visible member set
 * @returns Complete order with hidden members retained in their slots
 */
export const applyRelativePreferenceOperation = (
  currentOrder: readonly string[],
  operation: WatchlistPreferenceOperation
): readonly string[] => {
  const playerIds = new Set(operation.playerIds);
  const subset = currentOrder.filter(playerId => playerIds.has(playerId));
  const movedSubset = operation.moves.reduce<string[]>((order, move) => {
    const from = order.indexOf(move.playerId);
    if (from < 0) return order;
    const withoutPlayer = [...order.slice(0, from), ...order.slice(from + 1)];
    if (move.destination === "top") {
      return [move.playerId, ...withoutPlayer];
    }
    if (move.destination === "bottom") {
      return [...withoutPlayer, move.playerId];
    }
    const before = withoutPlayer.indexOf(move.destination.beforePlayerId);
    const destination = before < 0 ? withoutPlayer.length : before;
    return [
      ...withoutPlayer.slice(0, destination),
      move.playerId,
      ...withoutPlayer.slice(destination),
    ];
  }, subset);
  return applyPreferenceOperation(currentOrder, movedSubset);
};

/**
 * Projects one fetched page into the shared global order without reranking it.
 * @param items - Current filtered or paginated page
 * @param orderedPlayerIds - Authoritative global player order
 * @param getPlayerId - Stable player identity accessor
 * @returns The same page in global relative order, with unknown items stable
 */
export const orderItemsByPreference = <Item>(
  items: readonly Item[],
  orderedPlayerIds: readonly string[],
  getPlayerId: (item: Item) => string
): readonly Item[] => {
  if (orderedPlayerIds.length === 0) return items;
  const rank = new Map(
    orderedPlayerIds.map((playerId, index) => [playerId, index])
  );
  return items
    .map((item, index) => ({ item, index }))
    .sort(
      (left, right) =>
        (rank.get(getPlayerId(left.item)) ?? Number.MAX_SAFE_INTEGER) -
          (rank.get(getPlayerId(right.item)) ?? Number.MAX_SAFE_INTEGER) ||
        left.index - right.index
    )
    .map(({ item }) => item);
};

/**
 * Replays pending operations over the last authoritative server snapshot.
 * @param state - Current authoritative snapshot and pending operations
 * @returns Effective player order including optimistic operations
 */
export const projectPreferenceOrder = (
  state: WatchlistPreferenceOrderState
): readonly string[] =>
  state.operations.reduce<readonly string[]>(
    (order, operation) =>
      isPitchAssignmentOperation(operation) ||
      isPitchFormationOperation(operation)
        ? order
        : applyRelativePreferenceOperation(order, operation),
    state.confirmed.orderedPlayerIds
  );

/**
 * Replays formation changes over the confirmed formation.
 * @param state - Current authoritative snapshot and pending operations
 * @returns Effective formation including optimistic changes, if any is known
 */
export const projectPitchFormation = (
  state: WatchlistPreferenceOrderState
): string | undefined =>
  state.operations.reduce<string | undefined>(
    (formation, operation) =>
      isPitchFormationOperation(operation) ? operation.formation : formation,
    state.confirmed.formation
  );

/**
 * The operations with the rank order each one runs over — the confirmed
 * order plus the reorders queued before it — so a formation change
 * re-projects contested slots by the rank the backend will see (SE-8606).
 * @param state - Current authoritative snapshot and pending operations
 * @returns Each operation paired with the order it applies to
 */
const operationsWithOrder = (
  state: WatchlistPreferenceOrderState
): readonly {
  readonly operation: WatchlistPitchOperation;
  readonly order: readonly string[];
}[] =>
  state.operations.reduce<{
    readonly order: readonly string[];
    readonly steps: readonly {
      readonly operation: WatchlistPitchOperation;
      readonly order: readonly string[];
    }[];
  }>(
    ({ order, steps }, operation) => ({
      order:
        isPitchAssignmentOperation(operation) ||
        isPitchFormationOperation(operation)
          ? order
          : applyRelativePreferenceOperation(order, operation),
      steps: [...steps, { operation, order }],
    }),
    { order: state.confirmed.orderedPlayerIds, steps: [] }
  ).steps;

/**
 * Migrates assignments through one formation change, re-projecting when the
 * operation carries primary positions (SE-8606).
 * @param assignments - Effective assignments before the change
 * @param operation - The formation change
 * @param order - Rank order the change runs over
 * @returns Assignments after the change
 */
const applyFormationOperation = (
  assignments: Readonly<Record<string, WatchlistPitchAssignment>>,
  operation: WatchlistPitchFormationOperation,
  order: readonly string[]
): Readonly<Record<string, WatchlistPitchAssignment>> =>
  migratePitchAssignments(
    assignments,
    operation.slots,
    operation.primaryPositionByPlayer
      ? {
          rankedPlayerIds: order,
          primaryPositionByPlayer: operation.primaryPositionByPlayer,
        }
      : undefined
  );

export const projectPitchAssignments = (
  state: WatchlistPreferenceOrderState
): Readonly<Record<string, WatchlistPitchAssignment>> =>
  operationsWithOrder(state).reduce<
    Readonly<Record<string, WatchlistPitchAssignment>>
  >((assignments, { operation, order }) => {
    if (isPitchFormationOperation(operation)) {
      return applyFormationOperation(assignments, operation, order);
    }
    return isPitchAssignmentOperation(operation)
      ? {
          ...assignments,
          [operation.playerId]: {
            tacticalSlot: operation.tacticalSlot,
            origin: operation.tacticalSlot ? "SAVED" : "UNASSIGNED",
          },
        }
      : assignments;
  }, state.confirmed.assignmentsByPlayerId ?? {});

/**
 * The slot each placed member holds right now — what a formation operation
 * records as `previousSlotByPlayer` (SE-8391).
 * @param assignments - Effective assignments before the change
 * @returns Slot by player id, placed members only
 */
export const heldSlotsByPlayer = (
  assignments: Readonly<Record<string, WatchlistPitchAssignment>>
): Readonly<Record<string, string>> =>
  Object.fromEntries(
    Object.entries(assignments).flatMap(([playerId, assignment]) =>
      assignment.tacticalSlot ? [[playerId, assignment.tacticalSlot]] : []
    )
  );

/**
 * The slot each Unassigned member held before the formation change that
 * orphaned them (SE-8391): settled changes from the state, pending ones
 * replayed over it. A placement — or an explicit Unassigned — forgets the
 * entry, and so does a later change that re-projects the member into a free
 * slot (SE-8606); while they stay Unassigned, the entry stays.
 *
 * The slot a formation change took away is read from the assignments the
 * change is replayed OVER — the confirmed snapshot plus the operations before
 * it — never from the `previousSlotByPlayer` the operation was created with:
 * a held copy re-applied after a conflict runs over the other editor's
 * assignments, and the ones it recorded may no longer be anyone's.
 * @param state - Current authoritative snapshot and pending operations
 * @returns Former slot by player id, for members orphaned by a formation change
 */
export const projectPitchFormerSlots = (
  state: WatchlistPreferenceOrderState
): Readonly<Record<string, string>> => {
  const { formerSlots, assignments: projected } = operationsWithOrder(
    state
  ).reduce<{
    readonly formerSlots: Readonly<Record<string, string>>;
    readonly assignments: Readonly<Record<string, WatchlistPitchAssignment>>;
  }>(
    ({ formerSlots, assignments }, { operation, order }) => {
      if (isPitchFormationOperation(operation)) {
        const retained = new Set(operation.slots);
        const orphaned = Object.entries(heldSlotsByPlayer(assignments)).filter(
          ([, slot]) => !retained.has(slot)
        );
        return {
          formerSlots:
            orphaned.length === 0
              ? formerSlots
              : { ...formerSlots, ...Object.fromEntries(orphaned) },
          assignments: applyFormationOperation(assignments, operation, order),
        };
      }
      if (isPitchAssignmentOperation(operation)) {
        const { [operation.playerId]: _placed, ...rest } = formerSlots;
        return {
          formerSlots: operation.playerId in formerSlots ? rest : formerSlots,
          assignments: {
            ...assignments,
            [operation.playerId]: {
              tacticalSlot: operation.tacticalSlot,
              origin: operation.tacticalSlot ? "SAVED" : "UNASSIGNED",
            },
          },
        };
      }
      return { formerSlots, assignments };
    },
    {
      formerSlots: state.formerSlotByPlayerId ?? {},
      assignments: state.confirmed.assignmentsByPlayerId ?? {},
    }
  );
  // SE-8606: a member the re-projection placed again is not Unassigned, so
  // it names no former slot.
  return Object.fromEntries(
    Object.entries(formerSlots).filter(
      ([playerId]) => !projected[playerId]?.tacticalSlot
    )
  );
};

/**
 * Reorders one id within the currently rendered subset.
 * @param visiblePlayerIds - Player identities in their current visible order
 * @param playerId - Identity of the player being moved
 * @param destination - Named edge/step or zero-based destination index
 * @returns Visible player identities in their requested order
 */
export const moveVisiblePlayer = (
  visiblePlayerIds: readonly string[],
  playerId: string,
  destination: "top" | "up" | "down" | "bottom" | number
): readonly string[] => {
  const from = visiblePlayerIds.indexOf(playerId);
  if (from < 0) return visiblePlayerIds;
  const named = {
    top: 0,
    up: from - 1,
    down: from + 1,
    bottom: visiblePlayerIds.length - 1,
  } as const;
  const requested =
    typeof destination === "number" ? destination : named[destination];
  const to = Math.min(Math.max(requested, 0), visiblePlayerIds.length - 1);
  if (from === to) return visiblePlayerIds;
  const remaining = [
    ...visiblePlayerIds.slice(0, from),
    ...visiblePlayerIds.slice(from + 1),
  ];
  return [
    ...remaining.slice(0, to),
    visiblePlayerIds[from],
    ...remaining.slice(to),
  ];
};
